import cx from 'classnames'
import Styles from './PropertyForm.module.scss'
import { ReactComponent as ChevronDown } from 'assets/icons/chevron-down-properties.svg'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Pressable, usePress } from '@react-aria/interactions'

interface Props {
  selectedColor: string
  onSelect: (color: string) => void
}

const oldColors = [
  '#C1D4F8',
  '#C1E6F9',
  '#B4EEEE',
  '#FFE8AE',
  '#FFCFAD',
  '#FFBDB8',
  '#FCB3DE',
  '#D0AEF2',
  '#C2C7D1',
  '#223B91',
  '#287FBD',
  '#00A39E',
  '#EDB62C',
  '#ED7C2C',
  '#DD4740',
  '#E65CAC',
  '#8754BA',
  '#616875',
  '#1C266F',
  '#175A92',
  '#006361',
  '#B67D18',
  '#A24707',
  '#801C16',
  '#8C2360',
  '#4D2873',
  '#2B2F38',
  '#1C8640',
]

// NOTE: if you modify this array, don't forget to also modify its counterpart in
// https://github.com/almanaclabs/almanac-on-rails/blob/master/app/models/property_value.rb
// TODO: have a single source of truth
export const colors = [
  '#D6FFE6', // green-light
  '#D0F8F3', // teal-light
  '#CDF0FF', // cyan-light
  '#DAE6FF', // blue-light
  '#EBDCFF', // purple-light
  '#FFD6F9', // pink-light
  '#FFCBCB', // red-light
  '#FFD6B8', // orange-light
  '#FFEFC5', // yellow-light
  '#E5ECF3', // grey-light
  '#75E5A2', // green-bright
  '#8EF0E4', // teal-bright
  '#84E3F8', // cyan-bright
  '#9CCAFF', // blue-bright
  '#C698FF', // purple-bright
  '#FF99EF', // pink-bright
  '#FF9F9F', // red-bright
  '#FFA95A', // orange-bright
  '#FFD74A', // yellow-bright
  '#B1BECD', // grey-bright
  '#07BA4F', // green-saturated
  '#00BF9D', // teal-saturated
  '#00BAE2', // cyan-saturated
  '#273BF1', // blue-saturated
  '#8626FF', // purple-saturated
  '#E43CD5', // pink-saturated
  '#E53535', // red-saturated
  '#FA7514', // orange-saturated
  '#F3A100', // yellow-saturated
  '#717E97', // grey-saturated
  '#028C3A', // green-dark
  '#008575', // teal-dark
  '#00659E', // cyan-dark
  '#0026AD', // blue-dark
  '#5500BF', // purple-dark
  '#870061', // pink-dark
  '#870F0F', // red-dark
  '#BB4300', // orange-dark
  '#9C5400', // yellow-dark
  '#283144', // grey-dark
]

export const lightColors = [
  '#DAE6FF', // blue-light
  '#CDF0FF', // cyan-light
  '#D6FFE6', // green-light
  '#E5ECF3', // grey-light
  '#FFD6B8', // orange-light
  '#FFD6F9', // pink-light
  '#EBDCFF', // purple-light
  '#FFCBCB', // red-light
  '#D0F8F3', // teal-light
  '#FFEFC5', // yellow-light
]

export const lightToBrightMapping: { [key: string]: string } = {
  '#DAE6FF': '#9CCAFF', // blue-bright
  '#D6FFE6': '#75E5A2', // green-bright
  '#D0F8F3': '#8EF0E4', // teal-bright
  '#CDF0FF': '#84E3F8', // cyan-bright
  '#EBDCFF': '#C698FF', // purple-bright
  '#FFD6F9': '#FF99EF', // pink-bright
  '#FFCBCB': '#FF9F9F', // red-bright
  '#FFD6B8': '#FFA95A', // orange-bright
  '#FFEFC5': '#FFD74A', // yellow-bright
  '#E5ECF3': '#B1BECD', // grey-bright
}

export const brightToLightMapping: { [key: string]: string } = {
  '#9CCAFF': '#DAE6FF', // blue-light
  '#75E5A2': '#D6FFE6', // green-light
  '#8EF0E4': '#D0F8F3', // teal-light
  '#84E3F8': '#CDF0FF', // cyan-light
  '#C698FF': '#EBDCFF', // purple-light
  '#FF99EF': '#FFD6F9', // pink-light
  '#FF9F9F': '#FFCBCB', // red-light
  '#FFA95A': '#FFD6B8', // orange-light
  '#FFD74A': '#FFEFC5', // yellow-light
  '#B1BECD': '#E5ECF3', // grey-light
}

let fillColorsMapping: { [key: string]: string }
fillColorsMapping = {
  '#9CCAFF': '#000A1E', // blue-bright
  '#DAE6FF': '#002A7C', // blue-light
  '#84E3F8': '#001A03', // cyan-bright
  '#CDF0FF': '#00477B', // cyan-light
  '#75E5A2': '#001A03', // green-bright
  '#D6FFE6': '#005108', // green-light
  '#B1BECD': '#000715', // grey-bright
  '#E5ECF3': '#262B35', // grey-light
  '#FFA95A': '#220018', // orange-bright
  '#FFD6B8': '#612900', // orange-light
  '#FF99EF': '#220018', // pink-bright
  '#FFD6F9': '#750052', // pink-light
  '#C698FF': '#220018', // purple-bright
  '#EBDCFF': '#390083', // purple-light
  '#FF9F9F': '#220018', // red-bright
  '#FFCBCB': '#7C0000', // red-light
  '#8EF0E4': '#001A03', // teal-bright
  '#D0F8F3': '#004139', // teal-light
  '#FFD74A': '#220018', // yellow-bright
  '#FFEFC5': '#4A3600', // yellow-light
  '#283144': '#FFFFFF', // grey-dark
  '#717E97': '#FFFFFF', // grey-saturated
  '#0026AD': '#FFFFFF', // blue-dark
  '#273BF1': '#FFFFFF', // blue-saturated
  '#00659E': '#FFFFFF', // cyan-dark
  '#00BAE2': '#FFFFFF', // cyan-saturated
  '#028C3A': '#FFFFFF', // green-dark
  '#07BA4F': '#FFFFFF', // green-saturated
  '#BB4300': '#FFFFFF', // orange-dark
  '#FA7514': '#FFFFFF', // orange-saturated
  '#870061': '#FFFFFF', // pink-dark
  '#E43CD5': '#FFFFFF', // pink-saturated
  '#5500BF': '#FFFFFF', // purple-dark
  '#8626FF': '#FFFFFF', // purple-saturated
  '#870F0F': '#FFFFFF', // red-dark
  '#E53535': '#FFFFFF', // red-saturated
  '#008575': '#FFFFFF', // teal-dark
  '#00BF9D': '#FFFFFF', // teal-saturated
  '#9C5400': '#FFFFFF', // yellow-dark
  '#F3A100': '#FFFFFF', // yellow-saturated
}

// Map a color to its 'light' variant equivalent
let bgColorsMapping: { [key: string]: string }
bgColorsMapping = {
  '#9CCAFF': '#DAE6FF', // blue-bright
  '#DAE6FF': '#DAE6FF', // blue-light
  '#0026AD': '#DAE6FF', // blue-dark
  '#273BF1': '#DAE6FF', // blue-saturated
  '#84E3F8': '#CDF0FF', // cyan-bright
  '#CDF0FF': '#CDF0FF', // cyan-light
  '#00659E': '#CDF0FF', // cyan-dark
  '#00BAE2': '#CDF0FF', // cyan-saturated
  '#75E5A2': '#D6FFE6', // green-bright
  '#D6FFE6': '#D6FFE6', // green-light
  '#028C3A': '#D6FFE6', // green-dark
  '#07BA4F': '#D6FFE6', // green-saturated
  '#B1BECD': '#E5ECF3', // grey-bright
  '#E5ECF3': '#E5ECF3', // grey-light
  '#283144': '#E5ECF3', // grey-dark
  '#717E97': '#E5ECF3', // grey-saturated
  '#FFA95A': '#FFD6B8', // orange-bright
  '#FFD6B8': '#FFD6B8', // orange-light
  '#BB4300': '#FFD6B8', // orange-dark
  '#FA7514': '#FFD6B8', // orange-saturated
  '#FF99EF': '#FFD6F9', // pink-bright
  '#FFD6F9': '#FFD6F9', // pink-light
  '#870061': '#FFD6F9', // pink-dark
  '#E43CD5': '#FFD6F9', // pink-saturated
  '#C698FF': '#EBDCFF', // purple-bright
  '#EBDCFF': '#EBDCFF', // purple-light
  '#5500BF': '#EBDCFF', // purple-dark
  '#8626FF': '#EBDCFF', // purple-saturated
  '#FF9F9F': '#FFCBCB', // red-bright
  '#FFCBCB': '#FFCBCB', // red-light
  '#870F0F': '#FFCBCB', // red-dark
  '#E53535': '#FFCBCB', // red-saturated
  '#8EF0E4': '#D0F8F3', // teal-bright
  '#D0F8F3': '#D0F8F3', // teal-light
  '#008575': '#D0F8F3', // teal-dark
  '#00BF9D': '#D0F8F3', // teal-saturated
  '#FFD74A': '#FFEFC5', // yellow-bright
  '#FFEFC5': '#FFEFC5', // yellow-light
  '#9C5400': '#FFEFC5', // yellow-dark
  '#F3A100': '#FFEFC5', // yellow-saturated
}

const LIGHT_FILL_COLOR = '#FFFFFF'
const DARK_FILL_COLOR = '#2B2F38'

export const colorsDarkToLight = colors.slice().reverse()

export const getRandomColor = () => {
  return colors[Math.floor(Math.random() * colors.length)]
}

export const fillColor = (color: string | undefined) => {
  if (!color) return 'initial'

  // NOTE: if `color` is not in `colors`, indexOf will return -1
  if (colors.indexOf(color) === -1) {
    return oldColors.indexOf(color) < 9 ? DARK_FILL_COLOR : LIGHT_FILL_COLOR
  }
  return fillColorsMapping[color as keyof Object]
}

export const bgColor = (color: string | undefined) => {
  if (!color) return 'initial'

  // NOTE: if `color` is not in `colors`, indexOf will return -1
  if (colors.indexOf(color) === -1) return color
  return bgColorsMapping[color as keyof Object]
}

const colorClass = (color: string | undefined) => {
  if (!color) return 'fillDark'
  return colors.indexOf(color) < 20 ? 'fillDark' : 'fillLight'
}

const Color = ({ isSelected, color, onPress }: any) => {
  const { pressProps } = usePress({ onPress: () => onPress(color) })
  return (
    <div
      className={cx(Styles.colorInput, { [Styles.selected]: isSelected })}
      style={{ backgroundColor: color }}
      {...pressProps}
    />
  )
}

export const ColorInput = ({ selectedColor, onSelect }: Props) => {
  return (
    <DropdownTrigger containFocus={false} placement="bottom-start">
      <Pressable>
        <div className={Styles.colorDropdownWrapper}>
          <div className={Styles.colorDropdownTrigger} style={{ backgroundColor: selectedColor }}>
            <ChevronDown className={Styles[colorClass(selectedColor)]} />
          </div>
        </div>
      </Pressable>
      {(close) => (
        <div className={Styles.colorDropdownGrid}>
          {colorsDarkToLight.map((color) => (
            <Color
              key={color}
              isSelected={selectedColor === color}
              color={color}
              onPress={(color: string) => {
                onSelect(color)
                close()
              }}
            />
          ))}
        </div>
      )}
    </DropdownTrigger>
  )
}
